import { h, Fragment } from 'preact';
import { FunctionComponent, useEffect, useRef, useState } from 'preact/compat';
import './phone-input.css';

enum CountryField {
	iso2,
	dial_code,
	pattern,
	order_priority,
}

type LocalizeCallback = (iso2: string) => string;
type CountryPropsItem = [
	string,
	string,
		string | undefined,
	number
]
type Country = {
	iso2: string;
	dial_code: string;
	pattern: string | null;
	order_priority: number | null;
	name: string;
}
type PhoneInputProps = {
	value: string;
	countries: CountryPropsItem[];
	initialCountry: string;
	localize: (iso2: string) => string;
	preferredCountries?: string[];
	onChange: (phone: string, code: string) => void;
}

function last<T>(arr: T[]): T {
	return arr[arr.length - 1];
}

function formatPhone(pattern: string | null, text: string): string {
	if (!text || text.length === 0) {
		return '';
	}
	if (text.length < 2 || !pattern) {
		return text;
	}

	let { formattedText } = pattern.split('').reduce((acc, item) => {
		if (acc.remainingText.length === 0) {
			return acc;
		}

		if (item !== '.') {
			return {
				formattedText: acc.formattedText + item,
				remainingText: acc.remainingText,
			};
		}
		const [head, ...tail] = acc.remainingText;

		return {
			formattedText: acc.formattedText + head,
			remainingText: tail,
		};
	}, {
		formattedText: '',
		remainingText: text.split(''),
	});

	if (formattedText.includes('(') && !formattedText.includes(')')) {
		formattedText += ')';
	}
	return formattedText;
}

function normalizePhone(phone: string): string {
	return phone.replace(/\D/g, '');
}

function useEventListener(eventName: string, handler: EventListener, element = window) {
	const savedHandler = useRef<EventListener>();
	useEffect(() => {
		// @ts-ignore
		savedHandler.current = handler;
	}, [handler]);

	useEffect(() => {
			const isSupported = element && element.addEventListener;
			if (!isSupported) { return; }

			const eventListener = (event: Event) => {
				if (!savedHandler.current || !savedHandler.current) { return; }
				savedHandler.current(event);
			};

			element.addEventListener(eventName, eventListener);
			return () => element.removeEventListener(eventName, eventListener);
		}, [eventName, element],
	);
}

const getPlaceholder = (pattern: string | null) => {
	if (pattern === null) {
		return '01234 56789';
	}

	const { placeholder } = pattern
		.split('')
		.reduce((acc, item) => {
			if (item === '.') {
				const numbers = [...acc.numbers];

				const [num] = numbers.splice(0, 1);

				return {
					numbers,
					placeholder: acc.placeholder + num,
				};
			} else {
				return {
					numbers: acc.numbers,
					placeholder: acc.placeholder + item,
				};
			}
		}, {
			numbers: '01234567899876543210'.split(''),
			placeholder: '',
		});

	return placeholder;
};
const mapCountry = (localize: LocalizeCallback) => (item: CountryPropsItem): Country => ({
	iso2: item[CountryField.iso2],
	name: localize(item[CountryField.iso2]),
	dial_code: item[CountryField.dial_code],
	pattern: item[CountryField.pattern] || null,
	order_priority: item[CountryField.order_priority] || null,
});

export const PhoneInput: FunctionComponent<PhoneInputProps> = (props) => {
	const {
		value,
		localize,
		onChange,
		initialCountry,
		countries: rawCountries,
		preferredCountries: rawPreferredCountries = [],
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const flagButtonRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const codeRef = useRef<HTMLDivElement>(null);
	const countriesListRef = useRef<HTMLDivElement>(null);

	const [phone, updatePhone] = useState('');
	const [searchQuery, updateSearchQuery] = useState('');
	const [countries, updateCountries] = useState<Country[]>([]);
	const [country, updateCountry] = useState<Country | null>(null);
	const [countriesVisible, updateCountriesVisible] = useState(false);
	const [filteredCountries, updateFilteredCountries] = useState<Country[]>([]);
	const [preferredCountries, updatePreferredCountries] = useState<Country[]>([]);
	const [codeWidth, updateCodeWidth] = useState(0);

	const onChangeCountry = (iso2: string) => {
		const selectedCountry = countries
			.concat(preferredCountries)
			.find(item => item.iso2 === iso2);

		if (selectedCountry !== undefined) {
			updateCountry(selectedCountry);
			updateSearchQuery('');
			updateCountriesVisible(false);
		}
	};
	const handleDocumentClick = (e: Event) => {
		if (
			!countriesListRef.current ||
			!flagButtonRef.current
		) { return; }
		if (!countriesVisible) { return; }
		if (
			countriesListRef.current.contains(e.target as Node) ||
			flagButtonRef.current.contains(e.target as Node)
		) { return; }

		updateCountriesVisible(false);
		updateSearchQuery('');
	};
	const handleInputChange = (text: string) => {
		if (country === null) {
			updatePhone(text);
			return;
		}

		let formattedPhone = formatPhone(
			country.pattern,
			normalizePhone(text),
		);

		if (formattedPhone.length === phone.length && last(formattedPhone.split('')) === ')') {
			formattedPhone = formattedPhone.slice(0, -1);
		}

		updatePhone(formattedPhone);
	};

	useEventListener('mousedown', handleDocumentClick);

	useEffect(() => {
		const list: Country[] = rawCountries
			.map((item): Country => ({
				iso2: item[CountryField.iso2],
				name: localize(item[CountryField.iso2]),
				dial_code: item[CountryField.dial_code],
				pattern: item[CountryField.pattern] || null,
				order_priority: item[CountryField.order_priority] || null,
			}))
			.filter(item => !rawPreferredCountries.includes(item.iso2))
			.sort((a, b) => {
				if (a.name < b.name) { return -1; }
				if (a.name > b.name) { return 1; }
				return 0;
			});

		updateCountries(list);
		updatePreferredCountries(
			rawCountries
				.filter(item => rawPreferredCountries.includes(item[CountryField.iso2].toLowerCase()))
				.map(mapCountry(localize)),
		);

		onChangeCountry(initialCountry);
	}, []);
	useEffect(() => {
		if (!codeRef.current || !wrapperRef.current) { return; }

		const bounding = codeRef.current.getBoundingClientRect();
		const width = bounding.width + 'px';
		const variable = '--country-code-width';
		wrapperRef.current.style.setProperty(variable, width);
		updateCodeWidth(bounding.width);
	}, [country]);
	useEffect(() => {
		const filtered = countries
			.filter(item => item.name.toLowerCase().startsWith(searchQuery.trim().toLowerCase()));
		updateFilteredCountries(filtered);
	}, [countries, searchQuery]);
	useEffect(() => {
		if (
			country === null &&
			preferredCountries.length > 0 &&
			rawPreferredCountries.length > 0
		) {
			onChangeCountry(initialCountry);
		}
	}, [country, countries, preferredCountries, rawPreferredCountries, initialCountry]);
	useEffect(() => {
		const code = country ? country.dial_code : '';

		onChange(code + normalizePhone(phone), country ? country.iso2 : '');
	}, [phone]);
	useEffect(() => {
		if (!country) {
			return;
		}

		const regexp = new RegExp(`^${country.dial_code}`);
		const valuePhone = value.replace(regexp, '');

		if (valuePhone !== normalizePhone(phone)) {
			handleInputChange(valuePhone);
		}
	}, [value]);

	return (
		<div
			ref={wrapperRef}
			className="phone-input__wrapper"
		>
			<input
				type="text"
				value={phone}
				ref={inputRef}
				className="phone-input__input"
				onChange={(e: any) => handleInputChange(e.target.value)}
				placeholder={codeWidth > 0 ? getPlaceholder(country ? country.pattern : null) : ''}
			/>
			<div className="phone-input__country-code" ref={codeRef}>
				{country && '+' + country.dial_code}
			</div>
			<div
				className="phone-input__flag-container"
				ref={flagButtonRef}
				onClick={() => updateCountriesVisible(!countriesVisible)}
			>
				<i
					className={`flag flag-${country && country.iso2}`}
					style={{
						opacity: country ? 1 : 0,
					}}
				/>
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 8 }}>
					<path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#8C8C8C" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</div>
			{countriesVisible && (
				<div className={'countries_list'} ref={countriesListRef}>
					<div className={'countries_list__search-container'}>
						<div className="countries_list__input-container">
							<input
								type="text"
								onChange={(e: any) => {
									e.preventDefault();
									updateSearchQuery(e.target.value);
								}}
							/>
							<div className={'countries_list__input-icon'}>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<g clipPath="url(#countries_list__input)">
										<path
											d="M15.8906 14.6531L12.0969 10.8594C12.025 10.7875 11.9313 10.75 11.8313 10.75H11.4187C12.4031 9.60938 13 8.125 13 6.5C13 2.90937 10.0906 0 6.5 0C2.90937 0 0 2.90937 0 6.5C0 10.0906 2.90937 13 6.5 13C8.125 13 9.60938 12.4031 10.75 11.4187V11.8313C10.75 11.9313 10.7906 12.025 10.8594 12.0969L14.6531 15.8906C14.8 16.0375 15.0375 16.0375 15.1844 15.8906L15.8906 15.1844C16.0375 15.0375 16.0375 14.8 15.8906 14.6531ZM6.5 11.5C3.7375 11.5 1.5 9.2625 1.5 6.5C1.5 3.7375 3.7375 1.5 6.5 1.5C9.2625 1.5 11.5 3.7375 11.5 6.5C11.5 9.2625 9.2625 11.5 6.5 11.5Z"
											fill={'var(--input-icon)'}/>
									</g>
									<defs>
										<clipPath id="countries_list__input">
											<rect width="16" height="16" fill="white"/>
										</clipPath>
									</defs>
								</svg>

							</div>
						</div>
					</div>
					{preferredCountries.length > 0 && (
						<Fragment>
							{preferredCountries.map(item => (
								<div
									key={item.iso2}
									className={'countries_list__item ' + item.iso2}
									onClick={() => onChangeCountry(item.iso2)}
								>
									<i className={`flag flag-${item.iso2}`}/>
									{item.name}
									<span className={'countries_list__code'}>
								+{item.dial_code}
							</span>
								</div>
							))}
							<div className={'countries_list__line'}/>
						</Fragment>
					)}
					{filteredCountries.map((item, index) => (
						<div
							key={item.iso2}
							className={'countries_list__item ' + item.iso2}
							data-index={index}
							onClick={() => onChangeCountry(item.iso2)}
						>
							<i className={`flag flag-${item.iso2}`}/>
							{item.name}
							<span className={'countries_list__code'}>
								+{item.dial_code}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
